// src/components/test/TestSession.tsx
// Full-screen test runner. Inline subcomponents only — keep this file
// self-contained so it can be split later if it grows.
//
// Phases handled:
//   • 'loading'    → spinner shell
//   • 'active'     → question + side panel + footer Submit button
//   • 'finished'   → pass/fail results screen
//   • 'early_stop' → early stop screen (same shape as finished but different copy)
//   • 'error'      → error block

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTestStore } from '../../stores/useTestStore';
import { useThemePalette, type Palette } from '../../hooks/useThemePalette';
import { useThemeStore } from '../../stores/useThemeStore';
import type { QuestionResult, TestQuestion } from '../../types/test';

// ── Internal palette context — avoids calling useThemePalette() in 14 subcomponents ──
interface ThemeCtx extends Palette { isDark: boolean }
const TCtx = createContext<ThemeCtx>(null!);
const useC = () => useContext(TCtx);

// ── Inject keyframes once (mirrors HomePage pattern) ─────────────────────────
(function injectStyles() {
  if (typeof document === 'undefined') return;
  const id = 'esp-test-kf';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes tm-fade-in   { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    @keyframes tm-spin      { to { transform: rotate(360deg); } }
    @keyframes tm-pulse     { 0%,100% { opacity: 0.8; } 50% { opacity: 1; } }
    @keyframes tm-shake     { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }
  `;
  document.head.appendChild(s);
})();

// ─────────────────────────────────────────────────────────────────────────────
// Top-level component
// ─────────────────────────────────────────────────────────────────────────────

export default function TestSession() {
  const C      = useThemePalette();
  const isDark = useThemeStore((s) => s.theme) === 'dark';

  const phase           = useTestStore((s) => s.phase);
  const errorMessage    = useTestStore((s) => s.errorMessage);
  const reset           = useTestStore((s) => s.reset);
  const navigate        = useNavigate();

  // Lock body scroll while session is mounted.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleClose = () => {
    reset();
    navigate('/');
  };

  return (
    <TCtx.Provider value={{ ...C, isDark }}>
      <div
        style={{
          position:       'fixed',
          inset:          0,
          zIndex:         9999,
          background:     C.bg,
          color:          C.text,
          display:        'flex',
          flexDirection:  'column',
          overflow:       'hidden',
          fontFamily:     'inherit',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Test session"
      >
        {phase === 'loading'    && <LoadingState />}
        {phase === 'active'     && <ActiveState onClose={handleClose} />}
        {phase === 'finished'   && <ResultsState onClose={handleClose} kind="finished" />}
        {phase === 'early_stop' && <ResultsState onClose={handleClose} kind="early_stop" />}
        {phase === 'error'      && <ErrorState message={errorMessage} onClose={handleClose} />}
      </div>
    </TCtx.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading
// ─────────────────────────────────────────────────────────────────────────────

function LoadingState() {
  const C = useC();
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '20px',
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        border: `3px solid ${C.border}`,
        borderTopColor: C.accent,
        animation: 'tm-spin 0.8s linear infinite',
      }} />
      <p style={{ color: C.muted, fontSize: '0.85rem', margin: 0 }}>Loading test…</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Error
// ─────────────────────────────────────────────────────────────────────────────

function ErrorState({ message, onClose }: { message: string | null; onClose: () => void }) {
  const C = useC();
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '20px', padding: '40px',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: 'rgba(248,113,113,0.12)',
        border: '1px solid rgba(248,113,113,0.30)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '28px',
      }} aria-hidden="true">⚠</div>
      <h2 style={{ margin: 0, color: C.text, fontSize: '1.4rem' }}>Test Unavailable</h2>
      <p style={{ color: C.muted, fontSize: '0.95rem', textAlign: 'center', maxWidth: '480px', margin: 0 }}>
        {message || 'Something went wrong loading this test.'}
      </p>
      <PrimaryButton onClick={onClose}>Back to Course</PrimaryButton>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Active state — header / progress / question / side panel / footer
// ─────────────────────────────────────────────────────────────────────────────

function ActiveState({ onClose }: { onClose: () => void }) {
  const C = useC();
  const testData      = useTestStore((s) => s.testData);
  const questions     = useTestStore((s) => s.questions);
  const currentIndex  = useTestStore((s) => s.currentIndex);
  const answers       = useTestStore((s) => s.answers);
  const results       = useTestStore((s) => s.results);
  const previousBest  = useTestStore((s) => s.previousBest);
  const selectAnswer  = useTestStore((s) => s.selectAnswer);
  const submitAnswer  = useTestStore((s) => s.submitAnswer);

  const q = questions[currentIndex];

  const [feedback, setFeedback] = useState<{ correct: boolean; question: TestQuestion } | null>(null);

  const handleSubmit = () => {
    if (feedback || !q) return;
    const selected = answers[q.id];
    if (!selected) return;
    const correct = selected === q.correctAnswer;
    setFeedback({ correct, question: q });

    window.setTimeout(() => {
      submitAnswer();
      setFeedback(null);
    }, 900);
  };

  const stats = useMemo(() => deriveStats(questions, results), [questions, results]);
  const passing = testData?.metadata.passingScore ?? 80;

  if (!q || !testData) return <LoadingState />;

  const selectedKey = answers[q.id];
  const isLocked = !!feedback;

  const headerBg = C.isDark
    ? `linear-gradient(135deg, ${C.surface} 0%, rgba(13,18,32,0.85) 100%)`
    : `linear-gradient(135deg, ${C.surface} 0%, rgba(255,255,255,0.9) 100%)`;
  const footerBg = C.isDark ? 'rgba(8,12,20,0.7)' : 'rgba(248,250,252,0.9)';
  const trackBg  = C.isDark ? 'rgba(255,255,255,0.06)' : C.border;

  return (
    <>
      {/* Header */}
      <header style={{
        background: headerBg,
        borderBottom: `1px solid ${C.border}`,
        padding: '14px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0, gap: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
          <button
            onClick={onClose}
            aria-label="Exit test"
            title="Exit test"
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: C.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              border: `1px solid ${C.border}`,
              color: C.text,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px', fontFamily: 'inherit', flexShrink: 0,
            }}
          >✕</button>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{
              fontSize: '0.65rem', textTransform: 'uppercase',
              letterSpacing: '0.12em', color: C.muted, fontWeight: 700,
            }}>Test {testData.metadata.testVersion}</span>
            <h2 style={{
              margin: 0, fontSize: '1rem', fontWeight: 700, color: C.text,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '50vw',
            }}>{testData.metadata.title}</h2>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {previousBest && <PreviousBestPill best={previousBest} />}
          <StatChip color={C.success} label={`${stats.correct} Correct`} />
          <StatChip color={C.error}   label={`${stats.wrong} Wrong`} />
          <StatChip color="#fbbf24"   label={`${stats.remain} Left`} />
        </div>
      </header>

      {/* Progress bar */}
      <div style={{ padding: '12px 24px 0', flexShrink: 0 }}>
        <div style={{
          height: 6, borderRadius: 3,
          background: trackBg,
          overflow: 'hidden', position: 'relative',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            width: `${stats.progressPct}%`,
            background: `linear-gradient(90deg, ${C.accent} 0%, ${C.accent2} 100%)`,
            borderRadius: 3,
            transition: 'width 0.4s ease',
          }} />
        </div>
        <div style={{
          marginTop: 6, display: 'flex', justifyContent: 'space-between',
          fontSize: '0.7rem', color: C.muted, fontWeight: 600,
        }}>
          <span>Question {currentIndex + 1} of {questions.length}</span>
          <span>{stats.answered} / {questions.length}</span>
        </div>
      </div>

      {/* Body */}
      <div style={{
        flex: 1, display: 'flex', gap: 24, padding: '20px 24px',
        overflow: 'hidden', minHeight: 0,
      }}>
        {/* Question column */}
        <main style={{
          flex: '1 1 auto', minWidth: 0,
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: 16, padding: '24px 28px',
          overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 18,
        }}>
          <QuestionMeta q={q} index={currentIndex} total={questions.length} />
          <h3 style={{
            margin: 0, fontSize: '1.15rem', lineHeight: 1.5,
            color: C.text, fontWeight: 600,
          }}>{q.question}</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(q.options).map(([key, val]) => (
              <OptionButton
                key={key}
                optionKey={key}
                optionText={val}
                selected={selectedKey === key}
                disabled={isLocked}
                feedback={feedback ? {
                  isThisCorrect: key === q.correctAnswer,
                  isThisSelectedAndWrong: key === selectedKey && !feedback.correct,
                } : null}
                onClick={() => !isLocked && selectAnswer(q.id, key)}
              />
            ))}
          </div>

          {feedback && <FeedbackPanel correct={feedback.correct} q={feedback.question} />}
        </main>

        {/* Side panel */}
        <aside style={{
          flex: '0 0 240px', display: 'flex', flexDirection: 'column', gap: 16,
        }} className="esp-test-side">
          <InfoBox label="Pass Score" value={`${passing}%`} accent={C.accent} />
          <ScoreCircle pct={stats.scorePct} passing={passing} />
          {q.sectionTitle && (
            <div style={{
              padding: '10px 14px', borderRadius: 10,
              background: 'rgba(79,140,247,0.06)',
              border: '1px solid rgba(79,140,247,0.16)',
              fontSize: '0.72rem', color: C.muted, fontWeight: 600,
              letterSpacing: '0.04em',
            }}>
              <span style={{ display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: C.accent, marginBottom: 4 }}>
                Section
              </span>
              {q.sectionTitle}
            </div>
          )}
        </aside>
      </div>

      {/* Footer submit */}
      <footer style={{
        padding: '14px 24px',
        borderTop: `1px solid ${C.border}`,
        background: footerBg,
        display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
      }}>
        <PrimaryButton
          onClick={handleSubmit}
          disabled={!selectedKey || isLocked}
        >
          {isLocked ? '…' : 'Submit Answer'}
        </PrimaryButton>
      </footer>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stats chip / question meta / previous best pill
// ─────────────────────────────────────────────────────────────────────────────

function StatChip({ color, label }: { color: string; label: string }) {
  const C = useC();
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 999,
      background: C.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
      border: `1px solid ${C.border}`,
      fontSize: '0.7rem', fontWeight: 700, color: C.text,
      whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0,
      }} aria-hidden="true" />
      {label}
    </span>
  );
}

function QuestionMeta({ q, index, total }: { q: TestQuestion; index: number; total: number }) {
  const C = useC();
  const diffStyle: Record<string, React.CSSProperties> = {
    easy:   { color: C.success, background: 'rgba(52,211,153,0.10)', borderColor: 'rgba(52,211,153,0.28)' },
    medium: { color: '#fbbf24', background: 'rgba(251,191,36,0.10)', borderColor: 'rgba(251,191,36,0.28)' },
    hard:   { color: C.error,   background: 'rgba(248,113,113,0.10)', borderColor: 'rgba(248,113,113,0.28)' },
  };
  const ds = q.difficulty ? diffStyle[q.difficulty] || diffStyle.medium : null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{
        fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.12em',
        color: C.muted, fontWeight: 700,
      }}>
        Question {index + 1} of {total}
      </span>
      {ds && (
        <span style={{
          padding: '3px 10px', borderRadius: 999,
          fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', border: `1px solid ${ds.borderColor}`,
          background: ds.background, color: ds.color,
        }}>{q.difficulty}</span>
      )}
    </div>
  );
}

function PreviousBestPill({ best }: { best: { percentage: number; completedFlag: boolean } }) {
  const C = useC();
  const passed = best.completedFlag;
  return (
    <span
      title={`Your best score for this test: ${Math.round(best.percentage)}%`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 999,
        background: passed ? 'rgba(52,211,153,0.10)' : (C.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
        border: `1px solid ${passed ? 'rgba(52,211,153,0.30)' : C.border}`,
        fontSize: '0.7rem', fontWeight: 700,
        color: passed ? C.success : C.muted,
        whiteSpace: 'nowrap',
      }}>
      🏆 Best {Math.round(best.percentage)}%
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Option button
// ─────────────────────────────────────────────────────────────────────────────

interface OptionButtonProps {
  optionKey: string;
  optionText: string;
  selected: boolean;
  disabled: boolean;
  feedback: { isThisCorrect: boolean; isThisSelectedAndWrong: boolean } | null;
  onClick: () => void;
}

function OptionButton({ optionKey, optionText, selected, disabled, feedback, onClick }: OptionButtonProps) {
  const C = useC();
  const [hover, setHover] = useState(false);

  let bg          = C.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  let borderColor = C.border;
  let textColor   = C.text;

  if (feedback) {
    if (feedback.isThisCorrect) {
      bg = 'rgba(52,211,153,0.12)';
      borderColor = 'rgba(52,211,153,0.45)';
      textColor = C.success;
    } else if (feedback.isThisSelectedAndWrong) {
      bg = 'rgba(248,113,113,0.12)';
      borderColor = 'rgba(248,113,113,0.45)';
      textColor = C.error;
    } else {
      bg = C.isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)';
      textColor = C.muted;
    }
  } else if (selected) {
    bg = 'rgba(79,140,247,0.10)';
    borderColor = 'rgba(79,140,247,0.45)';
  } else if (hover) {
    bg = C.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
    borderColor = C.borderH;
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        width: '100%', padding: '14px 18px',
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 12,
        color: textColor,
        fontFamily: 'inherit', fontSize: '0.92rem',
        textAlign: 'left',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.18s ease, border-color 0.18s ease, color 0.18s ease',
        outline: 'none',
      }}
    >
      <span style={{
        width: 32, height: 32, borderRadius: '50%',
        background: feedback?.isThisCorrect
          ? C.success
          : feedback?.isThisSelectedAndWrong
          ? C.error
          : selected
          ? C.accent
          : C.isDark ? 'rgba(255,255,255,0.06)' : C.border,
        color: feedback || selected ? '#fff' : C.muted,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: '0.78rem', flexShrink: 0,
        textTransform: 'uppercase',
        transition: 'background 0.18s ease',
      }}>{optionKey}</span>
      <span style={{ flex: 1, lineHeight: 1.45 }}>{optionText}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Feedback panel
// ─────────────────────────────────────────────────────────────────────────────

function FeedbackPanel({ correct, q }: { correct: boolean; q: TestQuestion }) {
  const C = useC();
  return (
    <div style={{
      padding: '14px 18px', borderRadius: 12,
      background: correct ? 'rgba(52,211,153,0.08)' : 'rgba(248,113,113,0.08)',
      border: `1px solid ${correct ? 'rgba(52,211,153,0.30)' : 'rgba(248,113,113,0.30)'}`,
      display: 'flex', gap: 12, animation: 'tm-fade-in 0.25s ease both',
    }}>
      <span style={{ fontSize: '20px', flexShrink: 0 }} aria-hidden="true">
        {correct ? '✓' : '✗'}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: correct ? C.success : C.error, marginBottom: 4 }}>
          {correct ? 'Correct!' : 'Incorrect.'}
        </div>
        {!correct && (
          <div style={{ fontSize: '0.85rem', color: C.text, marginBottom: q.explanation ? 6 : 0 }}>
            Correct answer:{' '}
            <strong style={{ color: C.success }}>
              {q.correctAnswer.toUpperCase()}: {q.options[q.correctAnswer]}
            </strong>
          </div>
        )}
        {q.explanation && (
          <p style={{
            margin: 0, fontSize: '0.85rem', color: C.muted, lineHeight: 1.5,
          }}>{q.explanation}</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Side panel: info box + score circle
// ─────────────────────────────────────────────────────────────────────────────

function InfoBox({ label, value, accent }: { label: string; value: string; accent: string }) {
  const C = useC();
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 12,
      background: C.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      border: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <span style={{
        fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.12em',
        color: C.muted, fontWeight: 700,
      }}>{label}</span>
      <span style={{ fontSize: '1.4rem', fontWeight: 800, color: accent }}>{value}</span>
    </div>
  );
}

function ScoreCircle({ pct, passing }: { pct: number; passing: number }) {
  const C = useC();
  const size = 130, sw = 8;
  const r = (size - sw) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const off = circ - (pct / 100) * circ;
  const passColor = pct >= passing ? C.success : C.error;
  const trackColor = C.isDark ? 'rgba(255,255,255,0.06)' : C.border;

  return (
    <div style={{
      padding: 16, borderRadius: 12,
      background: C.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      border: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 8,
    }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={c} cy={c} r={r} fill="none"
            stroke={trackColor} strokeWidth={sw} />
          <circle cx={c} cy={c} r={r} fill="none"
            stroke={passColor} strokeWidth={sw} strokeLinecap="round"
            strokeDasharray={`${circ} ${circ}`} strokeDashoffset={off}
            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: '1.5rem', fontWeight: 800, color: C.text }}>
            {pct}%
          </span>
          <span style={{ fontSize: '0.6rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
            Score
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Results state — handles both 'finished' and 'early_stop'
// ─────────────────────────────────────────────────────────────────────────────

function ResultsState({ onClose, kind }: { onClose: () => void; kind: 'finished' | 'early_stop' }) {
  const C = useC();
  const testData      = useTestStore((s) => s.testData);
  const questions     = useTestStore((s) => s.questions);
  const results       = useTestStore((s) => s.results);
  const startTime     = useTestStore((s) => s.startTime);
  const retake        = useTestStore((s) => s.retake);

  const [showReview, setShowReview] = useState(false);

  if (!testData) return null;

  const stats = useMemo(() => deriveStats(questions, results), [questions, results]);
  const passing = testData.metadata.passingScore ?? 80;
  const total = questions.length;
  const correct = stats.correct;
  const answered = stats.answered;
  const pct = kind === 'finished'
    ? Math.round((correct / total) * 100)
    : answered > 0 ? Math.round((correct / answered) * 100) : 0;
  const passed = kind === 'finished' && pct >= passing;
  const elapsed = startTime ? Math.round((Date.now() - startTime) / 1000) : 0;
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  const sectionStats = useMemo(() => buildSectionBreakdown(results), [results]);

  const icon = passed ? '🏆' : kind === 'early_stop' ? '📖' : '📚';
  const title = passed
    ? 'Congratulations! You Passed!'
    : kind === 'early_stop' ? 'Test Stopped Early' : 'Keep Practising!';
  const subtitle = passed
    ? 'Excellent work. You have demonstrated strong understanding of this unit.'
    : kind === 'early_stop'
      ? `It is no longer mathematically possible to reach the passing score of ${passing}%.`
      : `You need ${passing}% to pass. Review the unit and try again.`;

  const accent = passed ? C.success : C.error;
  const cardBg  = C.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const footerBg = C.isDark ? 'rgba(8,12,20,0.5)' : 'rgba(248,250,252,0.8)';

  return (
    <div style={{
      flex: 1, overflow: 'auto', padding: '32px 24px',
      animation: 'tm-fade-in 0.4s ease both',
    }}>
      <div style={{
        maxWidth: 720, margin: '0 auto',
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 20, overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '36px 32px 28px',
          textAlign: 'center',
          background: passed
            ? 'linear-gradient(135deg, rgba(52,211,153,0.14) 0%, rgba(52,211,153,0.04) 100%)'
            : 'linear-gradient(135deg, rgba(248,113,113,0.12) 0%, rgba(248,113,113,0.03) 100%)',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ fontSize: '54px', marginBottom: 8 }} aria-hidden="true">{icon}</div>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.55rem', fontWeight: 800, color: C.text }}>
            {title}
          </h2>
          <p style={{ margin: 0, color: C.muted, fontSize: '0.95rem', lineHeight: 1.6 }}>
            {subtitle}
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Stat grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 12,
          }}>
            <ResultStat value={`${pct}%`} label={kind === 'early_stop' ? 'Current Score' : 'Final Score'} accent={accent} big />
            <ResultStat value={`${correct}/${kind === 'early_stop' ? answered : total}`} label="Correct" />
            {kind === 'early_stop' ? (
              <ResultStat value={`${total - answered}`} label="Skipped" />
            ) : (
              <ResultStat value={timeStr} label="Time Taken" />
            )}
            <ResultStat value={`${passing}%`} label="Pass Mark" />
          </div>

          {/* Section breakdown */}
          {sectionStats.length > 0 && (
            <div>
              <h3 style={{
                margin: '0 0 12px', fontSize: '0.78rem', textTransform: 'uppercase',
                letterSpacing: '0.12em', color: C.muted, fontWeight: 700,
              }}>
                Score by Section
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sectionStats.map((s) => (
                  <div key={s.title} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 16px', borderRadius: 10,
                    background: cardBg,
                    border: `1px solid ${C.border}`,
                  }}>
                    <span style={{ color: C.text, fontSize: '0.92rem', fontWeight: 600 }}>
                      {s.title}
                    </span>
                    <span style={{
                      color: s.pct >= passing ? C.success : C.error,
                      fontSize: '0.92rem', fontWeight: 700,
                    }}>
                      {s.correct}/{s.total} ({s.pct}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {kind === 'early_stop' && (
            <div style={{
              display: 'flex', gap: 12, alignItems: 'flex-start',
              padding: '14px 18px', borderRadius: 12,
              background: 'rgba(251,191,36,0.06)',
              border: '1px solid rgba(251,191,36,0.20)',
            }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }} aria-hidden="true">💡</span>
              <p style={{ margin: 0, color: C.muted, fontSize: '0.9rem', lineHeight: 1.5 }}>
                Don't be discouraged — review the unit materials and retake the test when you feel ready.
              </p>
            </div>
          )}

          {/* Review toggle */}
          <button
            onClick={() => setShowReview((v) => !v)}
            style={{
              padding: '12px 18px', borderRadius: 10,
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text, fontFamily: 'inherit',
              fontSize: '0.85rem', fontWeight: 700,
              letterSpacing: '0.04em', cursor: 'pointer',
              transition: 'background 0.18s ease, border-color 0.18s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = C.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
              e.currentTarget.style.borderColor = C.borderH;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = C.border;
            }}
          >
            {showReview ? 'Hide Answers' : kind === 'early_stop' ? 'Review Answers So Far' : 'Review All Answers'}
          </button>

          {showReview && <ReviewList results={results} />}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          borderTop: `1px solid ${C.border}`,
          background: footerBg,
          display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap',
        }}>
          <SecondaryButton onClick={onClose}>Back to Course</SecondaryButton>
          <PrimaryButton onClick={retake}>{kind === 'early_stop' ? 'Try Again' : 'Retake Test'}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ResultStat({ value, label, accent, big }: { value: string; label: string; accent?: string; big?: boolean }) {
  const C = useC();
  return (
    <div style={{
      padding: '16px 14px', borderRadius: 12,
      background: C.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      border: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 6, textAlign: 'center',
    }}>
      <span style={{
        fontSize: big ? '1.85rem' : '1.4rem',
        fontWeight: 800,
        color: accent || C.text,
        lineHeight: 1,
      }}>{value}</span>
      <span style={{
        fontSize: '0.65rem', textTransform: 'uppercase',
        letterSpacing: '0.12em', color: C.muted, fontWeight: 700,
      }}>{label}</span>
    </div>
  );
}

function ReviewList({ results }: { results: Record<string, QuestionResult> }) {
  const entries = Object.entries(results);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10,
      animation: 'tm-fade-in 0.3s ease both',
    }}>
      {entries.map(([id, r], i) => (
        <ReviewItem key={id} index={i} result={r} />
      ))}
    </div>
  );
}

function ReviewItem({ index, result }: { index: number; result: QuestionResult }) {
  const C = useC();
  return (
    <div style={{
      padding: 16, borderRadius: 12,
      background: result.correct ? 'rgba(52,211,153,0.06)' : 'rgba(248,113,113,0.06)',
      border: `1px solid ${result.correct ? 'rgba(52,211,153,0.22)' : 'rgba(248,113,113,0.22)'}`,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.12em',
          color: C.muted, fontWeight: 700,
        }}>Q{index + 1} · {result.sectionTitle}</span>
        <span style={{
          fontSize: '0.78rem', fontWeight: 800,
          color: result.correct ? C.success : C.error,
        }}>{result.correct ? '✓ Correct' : '✗ Incorrect'}</span>
      </div>
      <p style={{ margin: 0, color: C.text, fontSize: '0.92rem', lineHeight: 1.5, fontWeight: 500 }}>
        {result.question}
      </p>
      <div style={{ fontSize: '0.85rem', color: C.muted, lineHeight: 1.55 }}>
        <span>Your answer: </span>
        <strong style={{ color: result.correct ? C.success : C.error }}>
          {result.selected.toUpperCase()}: {result.options[result.selected] || '—'}
        </strong>
        {!result.correct && (
          <>
            <br />
            <span>Correct: </span>
            <strong style={{ color: C.success }}>
              {result.correctAnswer.toUpperCase()}: {result.options[result.correctAnswer]}
            </strong>
          </>
        )}
      </div>
      {result.explanation && (
        <p style={{
          margin: 0, padding: '8px 12px', borderRadius: 8,
          background: 'rgba(79,140,247,0.06)',
          border: '1px solid rgba(79,140,247,0.18)',
          fontSize: '0.82rem', color: C.muted, lineHeight: 1.55,
        }}>
          💡 {result.explanation}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Buttons
// ─────────────────────────────────────────────────────────────────────────────

function PrimaryButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  const C = useC();
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '12px 24px', borderRadius: 10,
        background: disabled
          ? (C.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)')
          : `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
        color: disabled ? C.muted : '#fff',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', fontSize: '0.85rem',
        fontWeight: 700, letterSpacing: '0.04em',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        transform: !disabled && hover ? 'translateY(-2px)' : 'none',
        boxShadow: !disabled && hover
          ? '0 10px 28px rgba(79,140,247,0.45)'
          : '0 4px 12px rgba(79,140,247,0.20)',
      }}
    >{children}</button>
  );
}

function SecondaryButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  const C = useC();
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '12px 24px', borderRadius: 10,
        background: hover
          ? (C.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)')
          : 'transparent',
        color: C.text,
        border: `1px solid ${hover ? C.borderH : C.border}`,
        cursor: 'pointer',
        fontFamily: 'inherit', fontSize: '0.85rem',
        fontWeight: 700, letterSpacing: '0.04em',
        transition: 'background 0.18s ease, border-color 0.18s ease',
      }}
    >{children}</button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

interface DerivedStats {
  correct: number;
  wrong: number;
  answered: number;
  remain: number;
  scorePct: number;
  progressPct: number;
}

function deriveStats(questions: TestQuestion[], results: Record<string, QuestionResult>): DerivedStats {
  const total = questions.length;
  const arr = Object.values(results);
  const correct = arr.filter((r) => r.correct).length;
  const wrong = arr.filter((r) => !r.correct).length;
  const answered = arr.length;
  const remain = total - answered;
  const scorePct = answered > 0 ? Math.round((correct / answered) * 100) : 0;
  const progressPct = total > 0 ? Math.round((answered / total) * 100) : 0;
  return { correct, wrong, answered, remain, scorePct, progressPct };
}

interface SectionRow { title: string; correct: number; total: number; pct: number; }

function buildSectionBreakdown(results: Record<string, QuestionResult>): SectionRow[] {
  const map = new Map<string, { correct: number; total: number }>();
  for (const r of Object.values(results)) {
    const key = r.sectionTitle || 'Other';
    const cur = map.get(key) || { correct: 0, total: 0 };
    cur.total += 1;
    if (r.correct) cur.correct += 1;
    map.set(key, cur);
  }
  return Array.from(map.entries()).map(([title, s]) => ({
    title,
    correct: s.correct,
    total: s.total,
    pct: s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0,
  }));
}